/**
 * Confirmation d’une action irréversible.
 *
 * Règle d’ARCHITECTURE §9.4 : une action destructive **récapitule ses
 * conséquences chiffrées** avant d’être exécutée. « Ouvrir la phase externe »
 * n’affiche pas « Êtes-vous sûr ? » mais :
 *
 *   37  propositions vont expirer
 *   61  sièges seront libérés
 *
 * Et là où la spec exige un motif (annulation de billet, de souscription, de
 * représentation, invitation, ajustement), le champ est ici, obligatoire.
 */

import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import type { DroitMetier } from '@tb/shared'

import { cn } from '../lib/cn'
import { Button } from './Button'
import { BandeauErreur } from './EmptyState'
import { FormField } from './FormField'
import { Modal } from './Modal'
import { Textarea } from './Textarea'

/** Longueur minimale d’un motif — alignée sur `motifSchema` (@tb/shared). */
const MOTIF_MIN = 3

export type TonConsequence = 'neutre' | 'avertissement' | 'danger'

export interface Consequence {
  /** Le chiffre : « 37 », « 61 », un `<Money>`… */
  readonly valeur: ReactNode
  /** Ce que ce chiffre signifie : « propositions vont expirer ». */
  readonly libelle: ReactNode
  readonly ton?: TonConsequence | undefined
}

const TONS_CONSEQUENCE: Readonly<Record<TonConsequence, string>> = {
  neutre: 'text-slate-900',
  avertissement: 'text-amber-700',
  danger: 'text-red-700',
}

export interface OptionsMotif {
  readonly label?: string | undefined
  readonly aide?: ReactNode | undefined
  readonly placeholder?: string | undefined
  /** `false` rend le motif facultatif (décliner une proposition — CAM-05). */
  readonly requis?: boolean | undefined
}

export interface ConfirmDialogProps {
  readonly permission?:DroitMetier | undefined
  readonly ouvert: boolean
  readonly titre: ReactNode
  readonly description?: ReactNode | undefined
  readonly extra?: ReactNode | undefined
  /** Le récapitulatif chiffré. Vide = action sans effet de bord mesurable. */
  readonly consequences?: readonly Consequence[] | undefined
  /** Demande un motif ; `{ requis: false }` le rend facultatif. */
  readonly motif?: OptionsMotif | undefined
  readonly confirmerLibelle?: string | undefined
  readonly annulerLibelle?: string | undefined
  readonly ton?: 'danger' | 'primaire' | undefined
  /** Mutation en cours : boutons neutralisés, spinner sur « Confirmer ». */
  readonly enCours?: boolean | undefined
  /** Message d’erreur du serveur, affiché **tel quel** (message métier). */
  readonly erreur?: string | null | undefined
  readonly onConfirmer: (motif: string) => void | Promise<void>
  readonly onAnnuler: () => void
}

export function ConfirmDialog({
  permission,
  ouvert,
  titre,
  description,
  extra,
  consequences,
  motif,
  confirmerLibelle = 'Confirmer',
  annulerLibelle = 'Annuler',
  ton = 'danger',
  enCours = false,
  erreur,
  onConfirmer,
  onAnnuler,
}: ConfirmDialogProps) {
  const [texteMotif, setTexteMotif] = useState('')
  const [touche, setTouche] = useState(false)

  // Chaque ouverture repart d’un motif vierge : on ne réutilise jamais celui
  // d’une action précédente.
  useEffect(() => {
    if (ouvert) {
      setTexteMotif('')
      setTouche(false)
    }
  }, [ouvert])

  const motifRequis = motif !== undefined && motif.requis !== false
  const motifValide = !motifRequis || texteMotif.trim().length >= MOTIF_MIN
  const erreurMotif =
    touche && !motifValide
      ? `Le motif est obligatoire (${String(MOTIF_MIN)} caractères minimum).`
      : null

  function confirmer(): void {
    setTouche(true)
    if (!motifValide || enCours) return
    void onConfirmer(texteMotif.trim())
  }

  return (
    <Modal
      ouvert={ouvert}
      onFermer={onAnnuler}
      titre={titre}
      taille="sm"
      fermetureExterieure={!enCours}
      pied={
        <>
          <Button variante="secondaire" onClick={onAnnuler} disabled={enCours}>
            {annulerLibelle}
          </Button>
          <Button permission={permission}
            variante={ton === 'danger' ? 'danger' : 'primaire'}
            onClick={confirmer}
            chargement={enCours}
          >
            {confirmerLibelle}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {description !== undefined && <p className="text-sm text-slate-700">{description}</p>}
        {extra}

        {consequences !== undefined && consequences.length > 0 && (
          <ul className="divide-y divide-slate-100 rounded-md border border-slate-200 bg-slate-50">
            {consequences.map((consequence, index) => (
              <li
                key={index}
                className="flex items-baseline gap-3 px-3 py-2 text-sm"
              >
                <span
                  className={cn(
                    'chiffres-alignes min-w-12 text-right text-lg font-semibold',
                    TONS_CONSEQUENCE[consequence.ton ?? 'neutre'],
                  )}
                >
                  {consequence.valeur}
                </span>
                <span className="text-slate-700">{consequence.libelle}</span>
              </li>
            ))}
          </ul>
        )}

        {motif !== undefined && (
          <FormField
            label={motif.label ?? 'Motif'}
            requis={motifRequis}
            aide={motif.aide}
            erreur={erreurMotif}
          >
            {(champ) => (
              <Textarea
                {...champ}
                value={texteMotif}
                placeholder={motif.placeholder ?? 'Ex. erreur de saisie'}
                invalide={erreurMotif !== null}
                disabled={enCours}
                onChange={(evenement) => { setTexteMotif(evenement.target.value) }}
                onBlur={() => { setTouche(true) }}
              />
            )}
          </FormField>
        )}

        {erreur !== null && erreur !== undefined && erreur !== '' && (
          <BandeauErreur message={erreur} />
        )}
      </div>
    </Modal>
  )
}
